import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Select, Input, InputNumber, message, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;
const { TextArea } = Input;

const AccessManagement = () => {
  const [requests, setRequests] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    fetchRequests();
    fetchDepartments();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/access-requests');
      console.log('[AccessManagement] Fetched access requests:', response.data);
      setRequests(response.data);
    } catch (error) {
      console.error('[AccessManagement] Failed to fetch access requests:', error);
      if (error.response) {
        console.error('[AccessManagement] Error response:', error.response);
        if (error.response.status === 401 || error.response.status === 403) {
          console.warn('[AccessManagement] Unauthorized, navigating to /profile');
          navigate('/profile');
        }
      }
      message.error('Failed to fetch access requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/api/departments');
      console.log('[AccessManagement] Fetched departments:', response.data);
      setDepartments(response.data);
    } catch (error) {
      console.error('[AccessManagement] Failed to fetch departments:', error);
      if (error.response) {
        console.error('[AccessManagement] Error response:', error.response);
        if (error.response.status === 401 || error.response.status === 403) {
          console.warn('[AccessManagement] Unauthorized, navigating to /profile');
          navigate('/profile');
        }
      }
      message.error('Failed to fetch departments');
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      await api.post('/api/access-requests', values);
      message.success('Access request submitted successfully');
      setModalVisible(false);
      form.resetFields();
      fetchRequests();
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to submit access request');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
    },
    {
      title: 'Duration (days)',
      dataIndex: 'duration',
      key: 'duration',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = 'default';
        switch (status) {
          case 'approved':
            color = 'success';
            break;
          case 'rejected':
            color = 'error';
            break;
          case 'pending':
            color = 'processing';
            break;
          default:
            color = 'default';
        }
        return <Tag color={color}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Resolved At',
      dataIndex: 'resolved_at',
      key: 'resolved_at',
      render: (date) => date ? new Date(date).toLocaleDateString() : '-',
    },
  ];

  console.log('[AccessManagement] Rendered with requests:', requests, 'departments:', departments);

  return (
    <Card title="Access Management">
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalVisible(true)}
        >
          Request Access
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={requests}
        rowKey="id"
        loading={loading}
        scroll={{ x: true }}
      />

      <Modal
        title="Request Access"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Please select a department' }]}
          >
            <Select placeholder="Select department">
              {departments.map(dept => (
                <Option key={dept.id} value={dept.id}>
                  {dept.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="duration"
            label="Duration (days)"
            rules={[
              { required: true, message: 'Please enter duration' },
              { type: 'number', min: 1, message: 'Duration must be at least 1 day' }
            ]}
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="Enter number of days" />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <TextArea
              rows={4}
              placeholder="Why do you need access to this department?"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default AccessManagement; 